import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import {
  ProjectExternalCameraActions,
} from './project-external-camera.actions';
import { ProjectExternalCameraModel } from '../../models/project-external-camera.model';
import { MODELS_PAGE_SIZE } from '../../../pages/project/pages/project-plan-view/modals/camera-list-modal/utils/cameras.const';

export interface PlanExternalCameraState
  extends EntityState<ProjectExternalCameraModel.CameraDocument> {
  isLoading: boolean;
  isBrandsLoading: boolean;
  isTypesLoading: boolean;
  isLoadingOnFilterChange: boolean;
  hasLoaded: boolean;
  cameraBrands: ProjectExternalCameraModel.CameraBrand[];
  cameraTypes: string[];
  selectedCamera: ProjectExternalCameraModel.CameraDocument | null;
  selectedCameraCount: number;
  page: number;
  perPage: number;
  isLastPage: boolean;
  filters: ProjectExternalCameraModel.API.FilterParams | null;
}

export const planExternalCameraAdapter: EntityAdapter<ProjectExternalCameraModel.CameraDocument> =
  createEntityAdapter<ProjectExternalCameraModel.CameraDocument>({
    selectId: (camera) => camera._id,
  });

const initialState: PlanExternalCameraState =
  planExternalCameraAdapter.getInitialState({
    isLoading: false,
    isBrandsLoading: false,
    isTypesLoading: false,
    isLoadingOnFilterChange: false,
    hasLoaded: false,
    cameraBrands: [],
    cameraTypes: [],
    selectedCamera: null,
    selectedCameraCount: 1,
    page: 0,
    perPage: MODELS_PAGE_SIZE,
    isLastPage: false,
    filters: null,
  });

export const planExternalCameraStateReducer = createReducer(
  initialState,
  on(ProjectExternalCameraActions.resetToInitialState, () => ({
    ...initialState,
  })),
  on(ProjectExternalCameraActions.loadExternalCameraBrands, (state) => ({
    ...state,
    isBrandsLoading: true,
  })),
  on(ProjectExternalCameraActions.loadExternalCameraBrandsFail, (state) => ({
    ...state,
    isBrandsLoading: false,
  })),
  on(ProjectExternalCameraActions.loadExternalCameraBrandsSuccess, (state, { brands }) => ({
    ...state,
    isBrandsLoading: false,
    cameraBrands: brands,
  })),
  on(ProjectExternalCameraActions.loadFilteredExternalCameras, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(ProjectExternalCameraActions.loadFilteredExternalCamerasFail, (state) => ({
    ...state,
    isLoading: false,
    hasLoaded: true,
    isLoadingOnFilterChange: false,
  })),
  on(ProjectExternalCameraActions.loadFilteredExternalCamerasSuccess, (state, { cameras }) => {
    return planExternalCameraAdapter.addMany(cameras, {
      ...state,
      isLastPage: cameras.length < state.perPage,
      page: state.page + 1,
      isLoading: false,
      hasLoaded: true,
      isLoadingOnFilterChange: false,
    });
  }),
  on(
    ProjectExternalCameraActions.setSelectedCamera,
    (state, { selectedCamera, count }) => ({
      ...state,
      selectedCamera,
      selectedCameraCount: selectedCamera ? Math.max(1, count ?? 1) : 1,
    }),
  ),
  on(ProjectExternalCameraActions.loadExternalCameraTypes, (state) => ({
    ...state,
    isTypesLoading: true,
  })),
  on(ProjectExternalCameraActions.loadExternalCameraTypesSuccess, (state, { types }) => ({
    ...state,
    isTypesLoading: false,
    cameraTypes: types,
  })),
  on(ProjectExternalCameraActions.loadExternalCameraTypesFail, (state) => ({
    ...state,
    isTypesLoading: false,
  })),
  on(ProjectExternalCameraActions.setExternalCameraFilters, (state, { filters }) => ({
    ...state,
    filters: filters,
    isLoadingOnFilterChange: true,
  })),
  on(ProjectExternalCameraActions.resetStateOnLastPage, (state) => ({
    ...state,
    hasLoaded: true,
    isLoading: false,
    isLoadingOnFilterChange: false,
  })),
  on(ProjectExternalCameraActions.resetList, (state) => {
    return planExternalCameraAdapter.removeAll({
      ...state,
      isLastPage: initialState.isLastPage,
      page: initialState.page,
    });
  }),
);

export const projectExternalCameraFeature = createFeature({
  name: 'planExternalCamera',
  reducer: planExternalCameraStateReducer,
  extraSelectors: ({ selectPlanExternalCameraState }) => {
    const { selectAll, selectEntities } =
      planExternalCameraAdapter.getSelectors(
        selectPlanExternalCameraState,
      );

    return {
      selectPlanExternalCameraEntities: selectEntities,
      selectPlanExternalCameras: selectAll,
      selectSelectedCameraCount: createSelector(
        selectPlanExternalCameraState,
        (state): number => state.selectedCameraCount,
      ),
      selectPlanExternalCameraById: (cameraId: string) =>
        createSelector(selectEntities, (entities) => {
          return entities[cameraId] ?? null;
        }),
    };
  },
});
